import axios, { AxiosHeaders, type AxiosError } from "axios";
import { apiConfig, getEffectiveApiBaseUrl } from "../config/api.config";
import {
  requestTokenRefresh,
  stopTokenRefreshMonitoring,
} from "../utils/tokenRefresh";

// Create axios instance with new configuration
// Create axios instance using effective baseURL (supports runtime override)
const effectiveBaseURL = getEffectiveApiBaseUrl();
console.log("🔧 Axios effective baseURL:", effectiveBaseURL);
const api = axios.create({
  baseURL: effectiveBaseURL,
  timeout: apiConfig.timeout,
  headers: apiConfig.headers,
});

// Add JWT token to headers if available
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem(ACCESS_TOKEN_KEY);
    if (token) {
      // Axios v1 expects AxiosHeaders; normalize and set safely.
      const headers = config.headers
        ? config.headers instanceof AxiosHeaders
          ? config.headers
          : new AxiosHeaders(config.headers as any)
        : new AxiosHeaders();

      headers.set("Authorization", `Bearer ${token}`);
      config.headers = headers;
    }

    return config;
  },
  (error) => {
    console.error("❌ Request error:", error);
    return Promise.reject(error);
  }
);

// Add a global response interceptor to handle 401 Unauthorized
// Use a flag to avoid multiple redirects
const ACCESS_TOKEN_KEY = "token";
const REFRESH_TOKEN_KEY = "refreshToken";

let isRedirectingToLogin = false;
let isRefreshing = false;
let refreshQueue: Array<{
  resolve: (token: string) => void;
  reject: (error: unknown) => void;
}> = [];

const clearAuthAndRedirect = (message?: string) => {
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  stopTokenRefreshMonitoring();
  if (!isRedirectingToLogin && typeof window !== "undefined") {
    isRedirectingToLogin = true;
    window.dispatchEvent(new CustomEvent("unauthorized", { detail: { message } }));
    setTimeout(() => {
      isRedirectingToLogin = false;
    }, 1000);
  }
};
api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error: AxiosError) => {
    console.error("🔍 Axios interceptor caught error:", {
      status: error?.response?.status,
      url: error?.config?.url,
      method: error?.config?.method,
      message: error.message,
      baseURL: error?.config?.baseURL,
      responseData: error?.response?.data,
    });

    const status = error.response?.status;
    const responseMessage =
      (error.response?.data as any)?.message ||
      (error.response?.data as any)?.error ||
      "";

    if (
      typeof responseMessage === "string" &&
      responseMessage.includes("Session expired due to inactivity")
    ) {
      clearAuthAndRedirect(
        "Session expired due to inactivity. Please login again."
      );
      return Promise.reject(error);
    }

    if (status === 401) {
      const originalRequest = error.config as any;
      if (originalRequest?._retry) {
        clearAuthAndRedirect("Session expired. Please login again.");
        return Promise.reject(error);
      }

      const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
      if (!refreshToken) {
        clearAuthAndRedirect("Session expired. Please login again.");
        return Promise.reject(error);
      }

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          refreshQueue.push({
            resolve: (token: string) => {
              originalRequest.headers = originalRequest.headers || {};
              originalRequest.headers["Authorization"] = `Bearer ${token}`;
              resolve(api.request(originalRequest));
            },
            reject,
          });
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const result = await requestTokenRefresh();
        if (!result?.accessToken) {
          clearAuthAndRedirect("Session expired. Please login again.");
          refreshQueue.forEach((p) => p.reject(error));
          refreshQueue = [];
          return Promise.reject(error);
        }

        localStorage.setItem(ACCESS_TOKEN_KEY, result.accessToken);
        if (result.refreshToken) {
          localStorage.setItem(REFRESH_TOKEN_KEY, result.refreshToken);
        }

        refreshQueue.forEach((p) => p.resolve(result.accessToken));
        refreshQueue = [];

        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers["Authorization"] = `Bearer ${result.accessToken}`;
        return api.request(originalRequest);
      } catch (refreshError) {
        refreshQueue.forEach((p) => p.reject(refreshError));
        refreshQueue = [];
        clearAuthAndRedirect("Session expired. Please login again.");
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export default api;
